<script>
  export let isOpen = false;
  import CookieBanner from "../ui/CookieBanner.svelte";
  import { slide, fade } from "svelte/transition";
  import { cubicOut } from "svelte/easing";
  import { userCountryStore, showCountryPopupStore, detectedCountryStore } from "#lib/stores/countryStore.js";
  import { onMount, onDestroy } from "svelte";

  let cookieBannerComponent;
  function toggleMenu() {
    isOpen = !isOpen;
  }

  // On the product page "Shop" pointed at the page itself, so it only closed the
  // menu (seen in a session replay, hub report 2026-10-26). There it now goes to
  // the setup choice; everywhere else it is the plain link it always was.
  // The scroll waits two frames: an open menu hides the Halloween bar, so the
  // header is 52px shorter until the menu has closed, and scrolling straight
  // away parked the heading under the bar once it came back.
  function handleShop(event) {
    isOpen = false;
    const target = document.getElementById("choose-setup");
    if (!target || window.location.pathname !== "/products/the-glorb/") return;
    event.preventDefault();
    requestAnimationFrame(() =>
      requestAnimationFrame(() => target.scrollIntoView({ behavior: "smooth", block: "start" })),
    );
  }

  function openCountrySelector() {
    isOpen = false;
    showCountryPopupStore.set(true);
  }

  function handleClick(event) {
    if (
      isOpen &&
      !event.target.closest(".nav-menu") &&
      !event.target.closest(".dot-container")
    ) {
      isOpen = false;
    }
  }

  // Toggle a global class to signal menu state (used to control banner visibility on product pages)
  onMount(() => {
    document.documentElement.classList.toggle('menu-open', isOpen);
  });

  $: {
    if (typeof document !== 'undefined' && document.documentElement) {
      document.documentElement.classList.toggle('menu-open', isOpen);
      // Prevent body scroll when menu is open
      if (isOpen) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    }
  }

  onDestroy(() => {
    if (typeof document !== 'undefined') {
      if (document.documentElement) {
        document.documentElement.classList.remove('menu-open');
      }
      // Restore body scroll when component unmounts
      document.body.style.overflow = '';
    }
  });

  $: countryDisplayName = $userCountryStore ? countryNames[$userCountryStore] || $userCountryStore : 'Select Country';
  const countryNames = {
    'AD': 'Andorra',
    'AL': 'Albania',
    'AM': 'Armenia',
    'AT': 'Austria',
    // 'AU': 'Australia',
    'BA': 'Bosnia and Herzegovina',
    'BE': 'Belgium',
    'BG': 'Bulgaria',
    'BY': 'Belarus',
    'CH': 'Switzerland',
    'CY': 'Cyprus',
    'CZ': 'Czech Republic',
    'DE': 'Germany',
    'DK': 'Denmark',
    'EE': 'Estonia',
    'GR': 'Greece',
    'ES': 'Spain',
    'FI': 'Finland',
    'FR': 'France',
    'GB': 'United Kingdom',
    'GE': 'Georgia',
    'HR': 'Croatia',
    'HU': 'Hungary',
    'IE': 'Ireland',
    'IS': 'Iceland',
    'IT': 'Italy',
    'KZ': 'Kazakhstan',
    'LI': 'Liechtenstein',
    'LT': 'Lithuania',
    'LU': 'Luxembourg',
    'LV': 'Latvia',
    'MC': 'Monaco',
    'MD': 'Moldova',
    'ME': 'Montenegro',
    'MK': 'North Macedonia',
    'MT': 'Malta',
    'NL': 'Netherlands',
    'NO': 'Norway',
    // 'NZ': 'New Zealand',
    'PL': 'Poland',
    'PT': 'Portugal',
    'RO': 'Romania',
    'RS': 'Serbia',
    'RU': 'Russia',
    'SE': 'Sweden',
    'SI': 'Slovenia',
    'SK': 'Slovakia',
    'SM': 'San Marino',
    'TR': 'Turkey',
    'UA': 'Ukraine',
    'US': 'United States',
    'VA': 'Vatican City',
    'XK': 'Kosovo'
  };

  
</script>

<svelte:window on:click|capture={handleClick} />

<!-- A real <button>: it was a div with role="button", tabindex and a hand-rolled
     Enter/Space handler. A native button gets keyboard activation, the right
     role, focus behaviour and a focus ring for free - and cannot drift out of
     sync with the ARIA it was imitating. -->
<button
  type="button"
  class="dot-container"
  on:click={() => (isOpen = !isOpen)}
  aria-expanded={isOpen}
  aria-label="Toggle menu"
>
  <div class="hamburger" class:open={isOpen}>
    <span class="bar"></span>
    <span class="bar"></span>
    <span class="bar"></span>
  </div>
</button>

{#if isOpen}
  <div
    class="nav-menu"
    in:slide|local={{
      duration: 1200,
      easing: cubicOut,
      axis: "y",
    }}
    out:slide|local={{
      duration: 0, // Faster slide out
      easing: cubicOut,
      axis: "y",
    }}
    role="dialog"
    aria-modal="true"
    aria-label="Navigation menu"
  >
    <nav
      in:fade={{
        duration: 400,
        delay: 400, // Start fading in after slide has started
        easing: cubicOut,
      }}
      out:fade={{
        duration: 100,
        easing: cubicOut,
      }}
    >
      <ul>
        <li><a class="shop-link" data-testid="menu-shop-link" href="/products/the-glorb/" on:click={handleShop}>Shop</a></li>

        <li><a href="/about/" on:click={toggleMenu}>Mission</a></li>

        <li><a href="/pages/app/" on:click={toggleMenu}>App & User Guide</a></li>
        <li>
          <a href="/pages/usermanual/" on:click={toggleMenu}>User Manual</a>
        </li>
        <li><a href="/faq/" on:click={toggleMenu}>Faq</a></li>
        <li><a href="/support/" on:click={toggleMenu}>Support</a></li>

        <li><a href="/b2b/" on:click={toggleMenu}>B2b</a></li>
        <li><a href="/contact/" on:click={toggleMenu}>Contact</a></li>
        <li> <button
          on:click={openCountrySelector}
          class="country-selector-btn"
          title="Change shipping country"
        >
          <span class="shipping-label">Ship to:</span>
          <span class="country-name">{countryDisplayName}</span>
       
        </button></li>
        <li>
          <button
            on:click={() => {
              cookieBannerComponent.toggleBanner();
              toggleMenu();
            }}
            class="cookie-button"
          >
            Cookie Settings
          </button>
        </li>
      </ul>
    </nav>
  </div>
{/if}

<CookieBanner bind:this={cookieBannerComponent} autoShow={false} />

<style lang="scss">
  /* Dot container styling */
  .dot-container {
    /* Now a real <button>, so undo the UA's chrome - the element has no visual
       styling of its own and must look exactly as it did as a div. */
    appearance: none;
    background: none;
    border: 0;
    margin: 0;
    padding: 0;
    color: inherit;
    font: inherit;
    /* A <button> is border-box in the UA stylesheet where a <div> is
       content-box, which shrank this by the width of its own padding. */
    box-sizing: content-box;

    width: 50px;
    height: var(--navbar-height);
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: inherit;
  }

  /* 3-bar hamburger that morphs into an X when open */
  .hamburger {
    position: relative;
    width: 24px;
    height: 16px;
  }

  .bar {
    position: absolute;
    left: 0;
    width: 100%;
    height: 2px;
    border-radius: 2px;
    background-color: currentColor;
    transition: top 0.3s ease, transform 0.3s ease, opacity 0.2s ease,
      background-color 0.3s ease;
  }
  .bar:nth-child(1) {
    top: 0;
  }
  .bar:nth-child(2) {
    top: 7px;
  }
  .bar:nth-child(3) {
    top: 14px;
  }

  /* Open state: top/bottom bars cross into an X, middle fades out.
     White so it stays visible on the dark full-screen menu. */
  .hamburger.open .bar {
    background-color: white;
  }
  .hamburger.open .bar:nth-child(1) {
    top: 7px;
    transform: rotate(45deg);
  }
  .hamburger.open .bar:nth-child(2) {
    opacity: 0;
  }
  .hamburger.open .bar:nth-child(3) {
    top: 7px;
    transform: rotate(-45deg);
  }

  nav {
    display: flex;
    justify-content: flex-start;
    height: 100%;
    align-items: center;
  }

  /* Full-screen navigation menu */
  .nav-menu {
    position: fixed;
    /* The header does not always start at the top of the viewport: the promo
       bar sits above it on the home and product pages, so its bottom edge is
       --navbar-height plus whatever the bar is rendering at. Header.svelte
       publishes that measured height; it is 0px when there is no bar, so pages
       without one are unchanged. Anchoring to --navbar-height alone is what
       slid SOLDR's panel 30px into its own header for 67 days
       (hub ledger 2026-09-21-064, rule R002 C1). */
    top: calc(var(--navbar-height) + var(--promo-bar-height, 0px));
    left: 0;
    width: 100vw;
    height: calc(100vh - var(--navbar-height) - var(--promo-bar-height, 0px));
    background-color: var(--secondary-color);
    color: white;
    display: flex;
    align-items: center;
    z-index: 1000;
    overflow-y: auto; /* Allow scrolling within menu if content is too long */
  }

  /* Show the menu only when open */

  .nav-menu nav ul {
    list-style: none;
    padding-left: var(--padding);
    margin: 0;
    text-align: left;
    -webkit-font-smoothing: antialiased;
  }

  .nav-menu nav ul li {
    margin-bottom: 10px;
  
  }
  

  .nav-menu nav ul li:first-child {
    margin-bottom:2rem;
 
  }

  .nav-menu nav ul li:nth-child(5) {
    margin-top: 2rem;
  }

  .nav-menu nav ul li:nth-child(7) {
    margin-top: 2rem;
  }

  .nav-menu nav ul li:nth-child(9) {
    margin-top: 2rem;
  }
  .nav-menu nav ul li:last-child {
    margin-top: 1rem;
  }

 .shop-link {
  color: white!important;
 }
  .nav-menu nav ul li a,
  .cookie-button {
    color:  #717171;;
    text-decoration: none;
    font-size: 1.5em;
    text-transform: uppercase;
    position: relative;
    background: none; /* Remove button background */
    border: none; /* Remove button border */
    padding: 0; /* Remove button padding */
    cursor: pointer; /* Add pointer cursor */
    font-family: inherit; /* Match font family */

    &:hover {
      color: white;
    }

    &::after {
      content: "";
      position: absolute;
      width: 100%;
      height: 1px;
      bottom: -4px;
      left: 0;
      background-color: white;
      transform: scaleX(0);
      transform-origin: right;
      transition: transform 0.3s ease;
    }

    &:hover::after {
      transform: scaleX(1);
      transform-origin: left;
   
    }
  }

  .country-name {
    color: white;;
  }

  .cookie-button {
    color: #717171;
    text-transform: none;
    font-size: 1rem;
  }

  .country-selector-btn {
    color: #717171;
    text-transform: none;
    font-size: 1rem;
    background-color: transparent;
    border: none;
    padding: 0;
    cursor: pointer;
    font-family: inherit;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;
  }
  

  @media (min-width: 860px) {
    .dot-container {
      padding-right: var(--padding);
      width: 35px;
    }

    .nav-menu nav ul li:nth-child(5) {
      margin-top: 3rem;
    }
    .nav-menu nav ul li:first-child {
      margin-bottom:3rem;
    }

    .nav-menu nav ul li:nth-child(7) {
      margin-top: 3rem;
    }
    .nav-menu nav ul li:nth-child(9) {
      margin-top: 3rem;
    }
    .nav-menu nav ul li:last-child {
      margin-top: 1rem;
    }

    .nav-menu nav ul li a {
      &::after {
        bottom: -6px; /* Slightly larger gap for desktop */
      }
    }
  }

  @media (max-width:400px) {
    .nav-menu nav ul li a {
      font-size: 1.2rem;
    }
  }
</style>
